import React, { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import '../CSS/ContactUs.css';

const ContactUs = () => {
  const form = useRef(null);

  const [expandedFaq, setExpandedFaq] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [submitStatus, setSubmitStatus] = useState('idle');

  const questions = [
    {
      question:
        'Do RFIs incur additional fees?',
      answer:
        'Some RFIs may incur additional fees depending on the size and scope of the request. Any applicable costs will be discussed and approved before work begins.',
    },
    {
      question:
        'I submitted an RFI. What happens next?',
      answer:
        'If your RFI is submitted during regular business hours, you can expect a confirmation the same business day. Requests submitted after business hours will typically receive a confirmation within one business day. After confirmation, an Intelligence Analyst will work with you to discuss any additional requirements or project details.',
    },
    {
      question: 'How long does it take to complete an RFI?',
      answer:
        'Completion times vary depending on the scope and complexity of the request. Simple requests may be completed within one business day, while larger research projects or analytical products may require additional time. Estimated timelines will be provided after the request is reviewed.',
    },
    {
      question: 'How often is Orion updated?',
      answer:
        'Tactical reports and analytical dashboards are updated daily.',
    },
    {
      question: 'How are reports collected for Orion?',
      answer:
        'INA Intelligence Analysts collect tactical and situational reports daily using a variety of OSINT methodologies and research tools. Information is gathered from publicly available sources and reviewed by analysts before publication.',
    },
    {
      question: 'Can I request a group profile that is not currently on Orion?',
      answer:
        'Yes. While new group profiles are added to Orion on a regular basis, you can also request profiles for specific organizations through an RFI.',
    },
    {
      question: 'Can I request assistance or training on using Orion?',
      answer:
        'Yes. Orion includes tutorials and training materials in the Media section. If you need additional assistance, Intelligence Analysts are available to provide guidance and answer specific questions upon request.',
    },
    {
      question: 'Can I schedule a meeting with an Intelligence Analyst?',
      answer:
        'Yes. Intelligence Analysts are available upon request to discuss specific issues, provide additional context, and answer questions about ongoing campaigns and reporting.',
    },
    {
      question: 'Can I suggest new features or data sources for Orion?',
      answer:
        'Yes. User feedback helps improve Orion. Suggestions for new dashboards, reports, datasets, or features can be submitted through an RFI or by contacting INA.',
    },
    {
      question: 'Our staff will be attending an event. Can INA provide special monitoring?',
      answer:
        'Yes. INA can provide event-specific monitoring for conferences, meetings, and other events where your personnel are in attendance. Monitoring may include local activist activity, planned demonstrations, civil unrest, travel disruptions, and other incidents that could affect attendees before or during the event.',
    },
    {
      question: 'Can INA provide support during high-risk events or incidents?',
      answer:
        'Yes. INA can provide enhanced monitoring, tailored reporting, and analyst support during major events, executive travel, conferences, protests, labor actions, or other situations requiring increased situational awareness.',
    },
  ];

  const requestCategories = [
    {
      title: 'Intelligence Assessment',
      icon: 'IA',
      summary:
        'A custom intelligence product focused on a specific topic, threat, trend, or operating concern.',
    },
    {
      title: 'Amplifying Information',
      icon: 'AI',
      summary:
        'Expanded context, sourcing, or analysis related to an existing Orion intelligence report.',
    },
    {
      title: 'Location Risk Assessment',
      icon: 'LR',
      summary:
        'An assessment of hazards, vulnerabilities, threat activity, and operating conditions at a location.',
    },
    {
      title: 'Executive / Individual Exposure Report',
      icon: 'ER',
      summary:
        'A review of public exposure, affiliations, risks, and relevant threat considerations involving an individual.',
    },
    {
      title: 'Organization Profile',
      icon: 'OP',
      summary:
        'An examination of an organization’s history, activity, structure, associations, and relevant risk indicators.',
    },
    {
      title: 'Investigative Support',
      icon: 'IS',
      summary:
        'Analyst support for identifying, validating, and organizing information relevant to an investigation.',
    },
    {
      title: 'Monitoring Request',
      icon: 'MR',
      summary:
        'Ongoing or event-based monitoring of selected organizations, individuals, locations, or issues.',
    },
    {
      title: 'Other',
      icon: 'OT',
      summary:
        'A custom request that does not fit one of the listed categories. Describe the desired outcome in the form.',
    },
  ];

  const sendEmail = async (event) => {
  event.preventDefault();
  setSubmitStatus('sending');

  try {
    if (!form.current) {
      throw new Error('Form reference is missing.');
    }

    const formData = new FormData(form.current);

    formData.append('service_id', 'service_dhgtf7o');
    formData.append('template_id', 'template_2oqlyys');
    formData.append('user_id', 'CSxb2cjoKlU_BpSCG');

    console.log('Sending RFI directly to EmailJS...');

    const response = await fetch(
      'https://api.emailjs.com/api/v1.0/email/send-form',
      {
        method: 'POST',
        body: formData,
      }
    );

    const responseText = await response.text();

    console.log('EmailJS status:', response.status);
    console.log('EmailJS response:', responseText);

    if (!response.ok) {
      throw new Error(
        `EmailJS returned ${response.status}: ${
          responseText || 'No response text'
        }`
      );
    }

    form.current.reset();
    setSubmitStatus('success');
  } catch (error) {
    console.error('RFI submission failed:', error);

    alert(
      error?.message ||
        'The request could not connect to EmailJS.'
    );

    setSubmitStatus('error');
  }
};

  const toggleFaq = (index) => {
    setExpandedFaq((current) => (current === index ? null : index));
  };

  const toggleCategory = (index) => {
    setExpandedCategory((current) =>
      current === index ? null : index
    );
  };

  return (
    <main className="rfi-page">
      <section className="rfi-hero" aria-labelledby="rfi-page-title">
        <div className="rfi-hero-copy">
          <span className="rfi-eyebrow">INTELLIGENCE SUPPORT</span>

          <h1 id="rfi-page-title">Request for Intelligence</h1>

          <p>
            Submit a Request for Intelligence (RFI) to receive analyst
            support for historical research, organization profiles,
            executive exposure assessments, monitoring requests, or custom
            intelligence products. Analysts typically acknowledge requests
            within one business day.
          </p>
        </div>

        <div className="rfi-hero-mark" aria-hidden="true">
          <span className="rfi-hero-mark-ring" />
          <span className="rfi-hero-mark-document" />
          <span className="rfi-hero-mark-line rfi-hero-mark-line-one" />
          <span className="rfi-hero-mark-line rfi-hero-mark-line-two" />
          <span className="rfi-hero-mark-check">✓</span>
        </div>
      </section>

      <section
        className="rfi-panel rfi-form-panel"
        aria-labelledby="rfi-form-title"
      >
        <div className="rfi-section-heading">
          <span className="rfi-section-kicker">
            ANALYST REQUEST PORTAL
          </span>

          <h2 id="rfi-form-title">Submit an Intelligence Request</h2>

          <p>
            Provide the information below so an analyst can review and
            scope your request.
          </p>
        </div>

        <form ref={form} onSubmit={sendEmail} className="rfi-form">
          <div className="rfi-form-grid">
            <div className="rfi-field rfi-field-full">
              <label htmlFor="rfi-request-type">Request type</label>

              <select
                id="rfi-request-type"
                name="dropdown"
                required
                defaultValue=""
              >
                <option value="" disabled>
                  Select a request type
                </option>

                {requestCategories.map((category) => (
                  <option
                    key={category.title}
                    value={category.title}
                  >
                    {category.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="rfi-field">
              <label htmlFor="rfi-name">Name</label>

              <input
                id="rfi-name"
                type="text"
                name="user_name"
                autoComplete="name"
                required
              />
            </div>

            <div className="rfi-field">
              <label htmlFor="rfi-email">Email</label>

              <input
                id="rfi-email"
                type="email"
                name="user_email"
                autoComplete="email"
                required
              />
            </div>

            <div className="rfi-field">
              <label htmlFor="rfi-company">Company</label>

              <input
                id="rfi-company"
                type="text"
                name="company"
                autoComplete="organization"
              />
            </div>

            <div className="rfi-field">
              <label htmlFor="rfi-deadline">
                Requested deadline
              </label>

              <input
                id="rfi-deadline"
                type="date"
                name="deadline"
              />
            </div>

            <div className="rfi-field rfi-field-full">
              <label htmlFor="rfi-subject">Subject</label>

              <input
                id="rfi-subject"
                type="text"
                name="subject"
                required
              />
            </div>

            <div className="rfi-field rfi-field-full">
              <label htmlFor="rfi-message">Request details</label>

              <textarea
                id="rfi-message"
                name="message"
                rows={8}
                placeholder="Describe the subject, scope, relevant dates, locations, known concerns, and the desired final product."
                required
              />
            </div>
          </div>

          <div className="rfi-form-footer">
            <div
              className="rfi-submit-message"
              aria-live="polite"
            >
              {submitStatus === 'success' &&
                'Your request was submitted successfully.'}

              {submitStatus === 'error' &&
                'The request could not be submitted. Please try again or contact INA directly.'}
            </div>

            <button
              type="submit"
              className="rfi-submit-button"
              disabled={submitStatus === 'sending'}
            >
              {submitStatus === 'sending'
                ? 'Submitting…'
                : 'Submit Request'}
            </button>
          </div>
        </form>
      </section>

      <section className="rfi-information-grid">
        <div className="rfi-panel rfi-information-panel">
          <div className="rfi-section-heading rfi-section-heading-compact">
            <span className="rfi-section-kicker">
              AVAILABLE SUPPORT
            </span>

            <h2>Request Categories</h2>

            <p>
              Select a category to review the type of analyst support
              available.
            </p>
          </div>

          <div className="rfi-accordion-list">
            {requestCategories.map((item, index) => {
              const isExpanded = expandedCategory === index;

              return (
                <article
                  className={`rfi-accordion-item ${
                    isExpanded ? 'is-expanded' : ''
                  }`}
                  key={item.title}
                >
                  <button
                    type="button"
                    className="rfi-accordion-trigger"
                    aria-expanded={isExpanded}
                    aria-controls={`category-panel-${index}`}
                    onClick={() => toggleCategory(index)}
                  >
                    <span
                      className="rfi-category-icon"
                      aria-hidden="true"
                    >
                      {item.icon}
                    </span>

                    <span className="rfi-accordion-title">
                      {item.title}
                    </span>

                    <span
                      className="rfi-expand-icon"
                      aria-hidden="true"
                    >
                      <span />
                      <span />
                    </span>
                  </button>

                  <div
                    id={`category-panel-${index}`}
                    className="rfi-accordion-content"
                    hidden={!isExpanded}
                  >
                    <p>{item.summary}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        <div className="rfi-panel rfi-information-panel">
          <div className="rfi-section-heading rfi-section-heading-compact">
            <span className="rfi-section-kicker">
              HELP CENTER
            </span>

            <h2>Frequently Asked Questions</h2>

            <p>
              Review common questions about requests, availability,
              timing, and pricing.
            </p>
          </div>

          <div className="rfi-accordion-list">
            {questions.map((item, index) => {
              const isExpanded = expandedFaq === index;

              return (
                <article
                  className={`rfi-accordion-item ${
                    isExpanded ? 'is-expanded' : ''
                  }`}
                  key={item.question}
                >
                  <button
                    type="button"
                    className="rfi-accordion-trigger"
                    aria-expanded={isExpanded}
                    aria-controls={`faq-panel-${index}`}
                    onClick={() => toggleFaq(index)}
                  >
                    <span
                      className="rfi-faq-icon"
                      aria-hidden="true"
                    >
                      ?
                    </span>

                    <span className="rfi-accordion-title">
                      {item.question}
                    </span>

                    <span
                      className="rfi-expand-icon"
                      aria-hidden="true"
                    >
                      <span />
                      <span />
                    </span>
                  </button>

                  <div
                    id={`faq-panel-${index}`}
                    className="rfi-accordion-content"
                    hidden={!isExpanded}
                  >
                    <p>{item.answer}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactUs;